<template>
  <div class="server-security-tab">
    <div class="server-security-tab__card">
      <div class="server-security-tab__card-head">
        <h4>Security audit</h4>
        <div class="server-security-tab__head-right">
          <span v-if="summaryLabel" class="server-security-tab__n">{{ summaryLabel }}</span>
          <span v-if="collectedAt" class="server-security-tab__fetched">fetched {{ formatRelativeTime(collectedAt) }}</span>
          <button
            type="button"
            class="server-security-tab__btn server-security-tab__btn--primary"
            :disabled="refreshing"
            @click="refresh"
          >{{ refreshing ? 'Running…' : 'Run audit' }}</button>
        </div>
      </div>
      <p v-if="error" class="server-security-tab__error">{{ error }}</p>

      <div v-if="loaded && !checks.length" class="server-security-tab__empty">
        No audit results yet — run an audit to ask the agent (requires agent v3.0.23-staging.11+).
      </div>

      <ul v-if="checks.length" class="server-security-tab__checks">
        <li v-for="row in unacceptedRows" :key="row.check.id" class="server-security-tab__check">
          <div class="server-security-tab__check-row">
            <span class="server-security-tab__status" :class="`is-${row.check.status}`">{{ row.check.status }}</span>
            <span class="server-security-tab__severity" :class="`is-${row.check.severity}`">{{ row.check.severity }}</span>
            <strong class="server-security-tab__label">{{ row.check.label }}</strong>
            <span class="server-security-tab__check-id">{{ row.check.id }}</span>
          </div>
          <div class="server-security-tab__detail">{{ row.check.detail }}</div>
          <pre v-if="row.check.evidence" class="server-security-tab__evidence">{{ formatEvidence(row.check.evidence) }}</pre>

          <div v-if="row.check.id === 'web.cf_client_ip_visibility' && row.check.status !== 'pass'" class="server-security-tab__cf-action">
            <button
              type="button"
              class="server-security-tab__btn"
              :disabled="cfApplying"
              @click="applyCfRealIp"
            >{{ cfApplying ? 'Applying…' : 'Restore real client IPs' }}</button>
            <span v-if="cfMessage" class="server-security-tab__cf-msg">{{ cfMessage }}</span>
          </div>

          <div v-if="row.remediation" class="server-security-tab__fix">
            <button
              type="button"
              class="server-security-tab__fix-toggle"
              :aria-expanded="isOpen(row.check.id) ? 'true' : 'false'"
              @click="toggle(row.check.id)"
            >
              <span class="server-security-tab__fix-caret" :class="{ 'is-open': isOpen(row.check.id) }">&#9656;</span>
              How to fix
            </button>

            <div v-if="isOpen(row.check.id)" class="server-security-tab__fix-body">
              <p class="server-security-tab__fix-summary">{{ row.remediation.summary }}</p>
              <p v-if="row.remediation.evidence_hint" class="server-security-tab__fix-hint">{{ row.remediation.evidence_hint }}</p>

              <div
                v-for="variant in visibleVariantsFor(row.remediation, osFamily)"
                :key="variant.key"
                class="server-security-tab__fix-variant"
              >
                <div v-if="variant.label" class="server-security-tab__fix-variant-label">{{ variant.label }}</div>
                <ol class="server-security-tab__fix-steps">
                  <li v-for="(step, i) in variant.steps" :key="i">
                    <span class="server-security-tab__fix-step-text">{{ step.text }}</span>
                    <code v-if="step.code" class="server-security-tab__fix-code">{{ step.code }}</code>
                  </li>
                </ol>
              </div>

              <p v-if="row.remediation.warning" class="server-security-tab__fix-warning">{{ row.remediation.warning }}</p>
            </div>
          </div>
        </li>
      </ul>

      <!-- Plan 012 §8.4 / Alerts-workspace brief Q1 — accepted checks move
           here instead of disappearing: read-only, with the reason and owner;
           the register (review, extend, revoke) is the Alerts page's
           Accepted tab. -->
      <div v-if="acceptedRows.length" class="server-security-tab__accepted">
        <div class="server-security-tab__accepted-head">
          <span>Accepted <span class="server-security-tab__n">{{ acceptedRows.length }}</span></span>
          <router-link class="server-security-tab__accepted-link" to="/servers/alerts?tab=accepted">Manage in Alerts → Accepted</router-link>
        </div>
        <ul class="server-security-tab__checks">
          <li v-for="row in acceptedRows" :key="row.check.id" class="server-security-tab__check server-security-tab__check--accepted">
            <div class="server-security-tab__check-row">
              <span class="server-security-tab__status" :class="`is-${row.check.status}`">{{ row.check.status }}</span>
              <span class="server-security-tab__severity" :class="`is-${row.check.severity}`">{{ row.check.severity }}</span>
              <strong class="server-security-tab__label">{{ row.check.label }}</strong>
              <span class="server-security-tab__check-id">{{ row.check.id }}</span>
            </div>
            <div class="server-security-tab__detail">
              Accepted<template v-if="row.acceptance.owner_name"> · {{ row.acceptance.owner_name }}</template>
              <template v-if="row.acceptance.review_at"> · review {{ new Date(row.acceptance.review_at * 1000).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) }}</template>
              <template v-if="row.acceptance.reason">: "{{ row.acceptance.reason }}"</template>
            </div>
          </li>
        </ul>
      </div>
    </div>

    <div class="server-security-tab__card">
      <div class="server-security-tab__card-head">
        <h4>Detections</h4>
        <div class="server-security-tab__head-right">
          <span v-if="detections.length" class="server-security-tab__n">{{ detections.length }} open/acknowledged</span>
        </div>
      </div>
      <p v-if="detectionsError" class="server-security-tab__error">{{ detectionsError }}</p>

      <div v-if="detectionsLoaded && !detections.length" class="server-security-tab__empty">
        No intrusion detections for this server — Tier 1 behavioural detection (Plan 008) alerts here when the agent reports one.
      </div>

      <ul v-if="detections.length" class="server-security-tab__detections">
        <li v-for="alert in detections" :key="alert.id" class="server-security-tab__detection">
          <div class="server-security-tab__detection-head">
            <AlertSeverityPill :severity="alert.severity" />
            <AlertStatusPill :status="alert.status" />
            <strong class="server-security-tab__detection-rule">{{ detectionRuleId(alert) }}</strong>
            <span class="server-security-tab__fetched">{{ formatRelativeTime(alert.opened_at) }}</span>
          </div>
          <div class="server-security-tab__detection-msg">{{ alert.message }}</div>

          <ul class="server-security-tab__finding-list">
            <li v-for="finding in findingsFor(alert)" :key="finding.key || finding.subtype || 'f'" class="server-security-tab__finding-row">
              <span v-if="finding.key" class="server-security-tab__finding-ip">{{ finding.key }}</span>
              <span v-if="finding.subtype" class="server-security-tab__finding-subtype">{{ finding.subtype }}</span>
              <span class="server-security-tab__finding-count">{{ finding.count }} in {{ finding.window_seconds }}s</span>
              <span v-if="finding.domain" class="server-security-tab__finding-domain">{{ finding.domain }}</span>
              <span v-if="finding.edge_only" class="server-security-tab__badge server-security-tab__badge--edge">edge-only</span>
              <span v-if="finding.guardian" class="server-security-tab__badge server-security-tab__badge--guardian">guardian</span>
              <span v-if="finding.last_seen" class="server-security-tab__fetched">last seen {{ formatRelativeTime(finding.last_seen) }}</span>
            </li>
          </ul>

          <div v-if="alert.status !== 'resolved'" class="server-security-tab__detection-actions">
            <button v-if="alert.status === 'open'" type="button" class="server-security-tab__btn" @click="ackDetection(alert)">Acknowledge</button>
            <button type="button" class="server-security-tab__btn server-security-tab__btn--primary" @click="resolveDetection(alert)">Resolve</button>
          </div>
        </li>
      </ul>
    </div>

    <div class="server-security-tab__card">
      <div class="server-security-tab__card-head">
        <h4>Block events</h4>
        <div class="server-security-tab__head-right">
          <span v-if="blockEvents.length" class="server-security-tab__n">
            {{ blockEvents.length }} recent<template v-if="simulatedCount"> · {{ simulatedCount }} simulated</template>
          </span>
        </div>
      </div>
      <p v-if="blockEventsError" class="server-security-tab__error">{{ blockEventsError }}</p>

      <div v-if="blockEventsLoaded && !blockEvents.length" class="server-security-tab__empty">
        No block events — fleet block rules ship in monitor mode, so this fills with “simulated” entries showing what would have been blocked.
      </div>

      <ul v-if="blockEvents.length" class="server-security-tab__block-events">
        <li v-for="event in blockEvents" :key="event.id" class="server-security-tab__block-event">
          <span class="server-security-tab__block-action" :class="`is-${event.action}`">{{ event.action }}</span>
          <span v-if="event.simulated" class="server-security-tab__badge server-security-tab__badge--simulated">would block</span>
          <span v-else-if="event.action === 'skipped'" class="server-security-tab__badge server-security-tab__badge--guardian">would NOT block</span>
          <span v-if="event.ip" class="server-security-tab__finding-ip">{{ event.ip }}</span>
          <span v-if="event.detection_rule_id" class="server-security-tab__finding-subtype">{{ event.detection_rule_id }}</span>
          <span v-if="event.backend" class="server-security-tab__finding-count">{{ event.backend }}</span>
          <span v-if="event.ttl_seconds" class="server-security-tab__finding-count">TTL {{ event.ttl_seconds }}s</span>
          <span v-if="event.detail" class="server-security-tab__finding-count">{{ event.detail }}</span>
          <span class="server-security-tab__fetched">{{ formatRelativeTime(event.occurred_at) }}</span>
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import useApi from '@apexl/icarus-core/src/services/api';
import useIntrospection from '@root/hooks/useIntrospection';
import { formatRelativeTime } from '@root/services/serverFormat';
import { parseMaybeJson } from '@root/services/alertFormat';
import { getRemediation, detectOsFamily, visibleVariantsFor } from '@root/components/Servers/securityRemediations';
import AlertSeverityPill from '@root/components/Alerts/AlertSeverityPill.vue';
import AlertStatusPill from '@root/components/Alerts/AlertStatusPill.vue';

// `server-detail.html`'s Security tab — renders the agent's `security.audit`
// result: one row per check with severity/status badges, detail text and an
// evidence block when present. Every non-passing check (fail/warn/error) also
// gets a collapsible "How to fix" panel driven by the app-side remediation
// catalog (`securityRemediations.js`). "Run audit" re-issues the command via
// the shared useIntrospection hook (result lands via the command.result
// webhook; failing checks also open an alert through the security_audit rule
// type).
//
// Plan 008 (Intrusion Detection & Fleet Auto-Block) Phase 1 adds two things
// to this same tab, per the plan's "no new top-level surface" call:
//  - a "Detections" panel below the audit card, reading the existing A4
//    alert feed filtered to `rule_type=intrusion` + this server — no new
//    backend table, the alert IS the finding record (see
//    `AlertEvaluationService::evaluateIntrusion()`'s docblock).
//  - a "Restore real client IPs" action on the `web.cf_client_ip_visibility`
//    audit check row (when it isn't passing) that dispatches the
//    `web.cf_realip.apply` command through the existing commands endpoint —
//    the check result itself is rendered no differently than any other
//    audit check above; this only adds the one action button.
const props = defineProps({
  apiBase: { type: String, required: true },
  serverId: { type: [Number, String], required: true },
  // The agent hello reports `os`/`kernel` but they aren't persisted on the
  // Server entity yet, so this is optional and usually null — the catalog then
  // renders both distro variants clearly labeled. Wired through so a real OS
  // string (if one ever lands on the server row) narrows to one variant.
  os: { type: String, default: null },
  alertingApiBase: { type: String, default: '/api/v1/observability/alerting' },
});

const api = useApi();

const { data, collectedAt, loaded, refreshing, error, load, refresh } = useIntrospection(props.apiBase, props.serverId, 'security.audit');

const checks = computed(() => (Array.isArray(data.value?.checks) ? data.value.checks : []));

const osFamily = computed(() => detectOsFamily(props.os));

// Decorate each check with its remediation entry (only for non-passing
// checks — passing checks need no fix guidance).
const rows = computed(() => checks.value.map((check) => ({
  check,
  remediation: check.status !== 'pass' ? getRemediation(check.id) : null,
})));

const openFixes = reactive({});
function toggle(id) {
  openFixes[id] = !openFixes[id];
}
function isOpen(id) {
  return !!openFixes[id];
}

const summaryLabel = computed(() => {
  if (!checks.value.length) {
    return '';
  }
  const passing = checks.value.filter((c) => c.status === 'pass').length;
  return `${passing} of ${checks.value.length} passing`;
});

function formatEvidence(evidence) {
  if (typeof evidence === 'string') {
    return evidence;
  }
  return JSON.stringify(evidence, null, 2);
}

// ── Restore real client IPs (web.cf_realip.apply) ──────────────────────────

const cfApplying = ref(false);
const cfMessage = ref('');

async function applyCfRealIp() {
  if (cfApplying.value) {
    return;
  }
  cfApplying.value = true;
  cfMessage.value = '';
  try {
    await api.post(`${props.apiBase}/${props.serverId}/commands`, { command: 'web.cf_realip.apply', payload: {} });
    cfMessage.value = 'Queued — re-run the audit shortly to confirm.';
  } catch (err) {
    cfMessage.value = err?.response?.data?.error || 'Could not queue the command.';
  } finally {
    cfApplying.value = false;
  }
}

// ── Detections panel (rides the existing A4 alert feed) ────────────────────

const detections = ref([]);
const detectionsLoaded = ref(false);
const detectionsError = ref(null);

async function loadDetections() {
  detectionsError.value = null;
  try {
    const data2 = await api.get(`${props.alertingApiBase}/alerts?server_id=${props.serverId}&rule_type=intrusion`);
    detections.value = (data2.alerts || []).filter((a) => a.status !== 'resolved');
  } catch {
    detections.value = [];
    detectionsError.value = 'Could not load intrusion detections.';
  } finally {
    detectionsLoaded.value = true;
  }
}

function detectionRuleId(alert) {
  const context = parseMaybeJson(alert.context, {});
  return context.detection_rule_id || '—';
}

/** @returns the context's `findings` map (keyed by IP/subtype) as a plain array, newest-updated first. */
function findingsFor(alert) {
  const context = parseMaybeJson(alert.context, {});
  const findings = context.findings && typeof context.findings === 'object' ? Object.values(context.findings) : [];
  return findings.slice().sort((a, b) => (b.updated_at || 0) - (a.updated_at || 0));
}

async function ackDetection(alert) {
  try {
    await api.post(`${props.alertingApiBase}/alerts/${alert.id}/ack`);
    await loadDetections();
  } catch { /* no-op — row stays as-is, user can retry */ }
}

async function resolveDetection(alert) {
  try {
    await api.post(`${props.alertingApiBase}/alerts/${alert.id}/resolve`);
    await loadDetections();
  } catch { /* no-op */ }
}

// ── Block events (Plan 008 §5.6, Phase 2) ──────────────────────────────────
//
// Read-only feed of what the agent's BlockManager did (or, in monitor mode,
// would have done). During the Phase-2 soak every row is `simulated` by
// construction — the "would block" badge is the load-bearing distinction on
// this card, which is why the API returns `simulated` per row rather than
// leaving the UI to infer it from the action string.
const blockEvents = ref([]);
const blockEventsLoaded = ref(false);
const blockEventsError = ref(null);
const simulatedCount = ref(0);

async function loadBlockEvents() {
  blockEventsError.value = null;
  try {
    const data3 = await api.get(`${props.apiBase}/${props.serverId}/block-events`);
    blockEvents.value = data3.events || [];
    simulatedCount.value = data3.simulated_count || 0;
  } catch {
    blockEvents.value = [];
    blockEventsError.value = 'Could not load block events.';
  } finally {
    blockEventsLoaded.value = true;
  }
}

// ── Accepted checks (Plan 012 Phase 2) ──────────────────────────────────
// Acceptances in force that cover this server, keyed by check id. A failing
// check that is accepted moves to the Accepted group; a passing one stays in
// the list (it is not alerting either way).
const acceptances = ref({});

async function loadAcceptances() {
  try {
    const data = await api.get(`${props.alertingApiBase}/suppressions?kind=accept&rule_type=security_audit&server_id=${props.serverId}`);
    const byCheck = {};
    (data.suppressions || []).forEach((s) => {
      if (s.finding_key) byCheck[s.finding_key] = s;
    });
    acceptances.value = byCheck;
  } catch {
    acceptances.value = {};
  }
}

const unacceptedRows = computed(() => rows.value.filter((row) => !(row.check.status !== 'pass' && acceptances.value[row.check.id])));
const acceptedRows = computed(() => rows.value
  .filter((row) => row.check.status !== 'pass' && acceptances.value[row.check.id])
  .map((row) => ({ ...row, acceptance: acceptances.value[row.check.id] })));

onMounted(() => {
  load();
  loadDetections();
  loadBlockEvents();
  loadAcceptances();
});
</script>

<style lang="scss">
.server-security-tab {
  display: flex;
  flex-direction: column;
  gap: 16px;

  &__card {
    background: var(--theme-bg-card);
    border: 1px solid var(--theme-border);
    border-radius: var(--theme-radius-lg, 10px);
    box-shadow: var(--theme-shadow-card);
    overflow: hidden;
  }

  &__accepted {
    border-top: 1px solid var(--theme-border-soft);
  }

  &__accepted-head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
    padding: 10px 18px 0;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: var(--theme-text-tertiary);
  }

  &__accepted-link {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0;
    text-transform: none;
    color: var(--theme-accent-dark);
    text-decoration: none;
  }

  &__check--accepted {
    opacity: 0.8;
  }

  &__card-head {
    padding: 14px 18px 10px;
    border-bottom: 1px solid var(--theme-border-soft);
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;

    h4 {
      margin: 0;
      font-size: 13.5px;
      font-weight: 600;
    }
  }

  &__head-right {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  &__n {
    font-size: 11px;
    color: var(--theme-text-tertiary);
  }

  &__fetched {
    font-size: 11px;
    color: var(--theme-text-tertiary);
  }

  &__btn {
    font-size: 11.5px;
    font-weight: 600;
    padding: 5px 12px;
    border-radius: var(--theme-radius, 6px);
    cursor: pointer;
    border: 1px solid var(--theme-border-strong);
    background: var(--theme-bg-card);

    &--primary {
      background: var(--theme-accent-primary);
      border-color: var(--theme-accent-primary);
      color: #fff;
    }

    &:disabled {
      opacity: .6;
      cursor: default;
    }
  }

  &__error {
    color: #a53232;
    font-size: 12px;
    padding: 10px 18px;
    margin: 0;
  }

  &__empty {
    color: var(--theme-text-tertiary);
    padding: 16px 18px;
    text-align: center;
    font-size: 12.5px;
  }

  &__checks {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  &__check {
    padding: 12px 18px;
    border-top: 1px solid var(--theme-border-soft);

    &:first-child {
      border-top: none;
    }
  }

  &__check-row {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }

  &__label {
    font-size: 12.5px;
  }

  &__check-id {
    font-family: var(--theme-font-mono);
    font-size: 10.5px;
    color: var(--theme-text-tertiary);
  }

  &__status {
    font-size: 10.5px;
    font-weight: 700;
    padding: 2px 8px;
    border-radius: 10px;
    text-transform: uppercase;
    background: var(--theme-border-soft);
    color: var(--theme-text-secondary);

    &.is-pass { background: #e1f3ea; color: #1c7a4d; }
    &.is-warn { background: #fbeeda; color: #8a5a0d; }
    &.is-fail { background: #f8dede; color: #a53232; }
    &.is-error { background: #f8dede; color: #a53232; }
  }

  &__severity {
    font-size: 10px;
    font-weight: 700;
    letter-spacing: .02em;
    padding: 2px 7px;
    border-radius: 3px;
    text-transform: uppercase;
    background: var(--theme-border-soft);
    color: var(--theme-text-secondary);

    &.is-warn { background: #fbeeda; color: #8a5a0d; }
    &.is-critical { background: #f8dede; color: #a53232; }
  }

  &__detail {
    margin-top: 5px;
    font-size: 12px;
    color: var(--theme-text-secondary);
  }

  &__evidence {
    margin: 8px 0 0;
    padding: 8px 10px;
    background: var(--theme-bg-content);
    border: 1px solid var(--theme-border-soft);
    border-radius: var(--theme-radius, 6px);
    font-family: var(--theme-font-mono);
    font-size: 11px;
    color: var(--theme-text-secondary);
    white-space: pre-wrap;
    word-break: break-word;
    max-height: 220px;
    overflow: auto;
  }

  &__fix {
    margin-top: 8px;
  }

  &__fix-toggle {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 11.5px;
    font-weight: 600;
    color: var(--theme-accent-dark);
    background: none;
    border: none;
    padding: 2px 0;
    cursor: pointer;
  }

  &__fix-caret {
    display: inline-block;
    font-size: 10px;
    transition: transform .12s ease;

    &.is-open {
      transform: rotate(90deg);
    }
  }

  &__fix-body {
    margin-top: 8px;
    padding: 12px 14px;
    background: var(--theme-bg-content);
    border: 1px solid var(--theme-border-soft);
    border-radius: var(--theme-radius, 6px);
  }

  &__fix-summary {
    margin: 0;
    font-size: 12px;
    color: var(--theme-text-secondary);
  }

  &__fix-hint {
    margin: 6px 0 0;
    font-size: 11.5px;
    font-style: italic;
    color: var(--theme-text-tertiary);
  }

  &__fix-variant {
    margin-top: 10px;
  }

  &__fix-variant-label {
    font-size: 10px;
    font-weight: 700;
    letter-spacing: .05em;
    text-transform: uppercase;
    color: var(--theme-text-tertiary);
    margin-bottom: 4px;
  }

  &__fix-steps {
    margin: 0;
    padding-left: 18px;

    li {
      font-size: 12px;
      color: var(--theme-text-secondary);
      margin-bottom: 6px;

      &:last-child {
        margin-bottom: 0;
      }
    }
  }

  &__fix-code {
    display: block;
    margin-top: 4px;
    padding: 5px 8px;
    background: var(--theme-bg-card);
    border: 1px solid var(--theme-border-soft);
    border-radius: var(--theme-radius-sm, 4px);
    font-family: var(--theme-font-mono);
    font-size: 11px;
    color: var(--theme-text-primary);
    white-space: pre-wrap;
    word-break: break-word;
  }

  &__fix-warning {
    margin: 10px 0 0;
    padding: 8px 10px;
    background: #fbeeda;
    border-radius: var(--theme-radius-sm, 4px);
    font-size: 11.5px;
    color: #8a5a0d;
    white-space: pre-line;
  }

  &__cf-action {
    margin-top: 10px;
    display: flex;
    align-items: center;
    gap: 10px;
  }

  &__cf-msg {
    font-size: 11.5px;
    color: var(--theme-text-tertiary);
  }

  &__detections {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  &__detection {
    padding: 12px 18px;
    border-top: 1px solid var(--theme-border-soft);

    &:first-child {
      border-top: none;
    }
  }

  &__detection-head {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }

  &__detection-rule {
    font-family: var(--theme-font-mono);
    font-size: 12px;
  }

  &__detection-msg {
    margin-top: 5px;
    font-size: 12.5px;
    color: var(--theme-text-primary);
  }

  &__finding-list {
    list-style: none;
    margin: 8px 0 0;
    padding: 0;
  }

  &__finding-row {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    padding: 5px 0;
    font-size: 11.5px;
    color: var(--theme-text-secondary);

    &:not(:first-child) {
      border-top: 1px dashed var(--theme-border-soft);
    }
  }

  &__finding-ip {
    font-family: var(--theme-font-mono);
    font-weight: 600;
    color: var(--theme-text-primary);
  }

  &__finding-subtype,
  &__finding-domain {
    font-family: var(--theme-font-mono);
  }

  &__finding-count {
    color: var(--theme-text-tertiary);
  }

  &__badge {
    font-size: 10px;
    font-weight: 700;
    letter-spacing: .02em;
    text-transform: uppercase;
    padding: 2px 7px;
    border-radius: 3px;

    &--edge {
      background: #ddeaf4;
      color: #215d84;
    }

    &--guardian {
      background: #efe6f5;
      color: #6b3e94;
    }

    &--simulated {
      background: #e6eef5;
      color: #37567a;
    }
  }

  &__block-events {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  &__block-event {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    padding: 9px 18px;
    border-top: 1px solid var(--theme-border-soft);
    font-size: 11.5px;
    color: var(--theme-text-secondary);

    &:first-child {
      border-top: none;
    }
  }

  &__block-action {
    font-size: 10.5px;
    font-weight: 700;
    padding: 2px 8px;
    border-radius: 10px;
    text-transform: uppercase;
    background: var(--theme-border-soft);
    color: var(--theme-text-secondary);

    &.is-block { background: #f8dede; color: #a53232; }
    &.is-unblock,
    &.is-expire { background: #e1f3ea; color: #1c7a4d; }
    &.is-flush { background: #fbeeda; color: #8a5a0d; }
    &.is-skipped { background: #efe6f5; color: #6b3e94; }
    &.is-error { background: #f8dede; color: #a53232; }
  }

  &__detection-actions {
    display: flex;
    gap: 8px;
    margin-top: 10px;
  }
}
</style>
